import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const AREAS = [
  {
    code: "EB-1A",
    tagline: "Extraordinary Ability · Green Card",
    intro: "The EB-1A category is reserved for the very top of a given field — sustained national or international acclaim, evidence of a level of expertise indicating you're one of the small percentage at the pinnacle of your discipline.",
    criteria: [
      "Awards for excellence",
      "Published material about you in professional or major media",
      "Original scientific, scholarly, or business-related contributions of major significance",
      "Authorship of scholarly articles",
      "High salary or remuneration",
      "Commercial successes in the performing arts",
      "Judging the work of others",
      "Membership in associations requiring outstanding achievement",
      "Employment in a critical role for organizations of distinguished reputation",
      "Display of your work at artistic exhibitions",
    ],
    idealFor: "Senior researchers, distinguished founders, principal engineers with major publications, award-winning artists.",
  },
  {
    code: "EB-2 NIW",
    tagline: "National Interest Waiver · Green Card",
    intro: "The NIW allows advanced-degree professionals to bypass the labor certification when their work is of substantial merit and national importance — and when it's in the U.S. national interest to waive the standard employer sponsorship.",
    criteria: [
      "Advanced degree (or exceptional ability) in your field",
      "Proposed endeavor has substantial merit and national importance",
      "You are well positioned to advance the endeavor",
      "On balance, waiving the standard job offer + labor cert benefits the U.S.",
    ],
    idealFor: "PhD researchers, entrepreneurs building U.S. companies, engineers working on critical infrastructure, healthcare and public-health professionals.",
  },
  {
    code: "O-1",
    tagline: "Extraordinary Ability Visa · Non-Immigrant",
    intro: "The O-1 is a temporary work visa for individuals with extraordinary ability in the sciences, education, business, athletics (O-1A) or arts, motion picture, television (O-1B). It requires a U.S. petitioner and offers a faster path than EB-1A.",
    criteria: [
      "Sustained national or international acclaim",
      "At least 3 of the 8 evidentiary criteria met (O-1A)",
      "Awards, publications, high salary, critical role, judging, memberships, media, original contributions",
      "A U.S. employer or agent to file the petition",
    ],
    idealFor: "Startup founders being sponsored by their own U.S. company, artists on tour, athletes, researchers moving to U.S. institutions.",
  },
];

export default function PracticeAreas() {
  return (
    <div data-testid="practice-areas-page">
      <section className="container-x pt-24 pb-16">
        <div className="max-w-3xl">
          <div className="eyebrow fade-up">Practice Areas</div>
          <h1 className="fade-up d-100 font-display text-5xl lg:text-6xl mt-6 tracking-tight text-[#0F172A] leading-none">
            Three petitions.
            <br />
            <span className="italic text-[#C2A679]">Uncommon</span> depth.
          </h1>
          <p className="fade-up d-200 mt-8 text-lg text-[#4A4D50] leading-relaxed">
            We do not maintain a general immigration practice. Our attention is undivided across
            three of the most demanding employment-based categories in U.S. law.
          </p>
        </div>
      </section>

      {AREAS.map((a, idx) => (
        <section
          key={a.code}
          data-testid={`practice-section-${a.code}`}
          className={`py-24 lg:py-32 ${idx % 2 === 1 ? "bg-[#EBE8DF]/50 border-t border-b border-[#0F172A]/10" : ""}`}
        >
          <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="sticky top-28">
                <div className="font-mono-plex text-xs tracking-[0.28em] uppercase text-[#C2A679]">
                  0{idx + 1} / {a.code}
                </div>
                <h2 className="font-display text-4xl lg:text-5xl mt-3 leading-none text-[#0F172A]">{a.code}</h2>
                <div className="mt-4 text-sm text-[#4A4D50]">{a.tagline}</div>
              </div>
            </div>
            <div className="md:col-span-8">
              <p className="text-lg text-[#1A1D20] leading-relaxed">{a.intro}</p>
              <div className="mt-12">
                <div className="eyebrow">Evidentiary criteria</div>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  {a.criteria.map((c, i) => (
                    <li key={i} className="py-4 border-t border-[#0F172A]/15 text-[#1A1D20] flex gap-3">
                      <span className="font-mono-plex text-xs text-[#C2A679] pt-1">{String(i + 1).padStart(2, "0")}</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-12 bg-white border border-[#0F172A]/10 p-8">
                <div className="eyebrow">Ideal candidate</div>
                <p className="mt-3 text-[#1A1D20] leading-relaxed">{a.idealFor}</p>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="container-x py-24">
        <div className="bg-[#0F172A] text-[#F7F5F0] p-12 md:p-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <div className="eyebrow text-[#C2A679]">Not sure which category fits?</div>
            <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">A $200 case review will tell you exactly.</h3>
          </div>
          <Link to="/case-review" data-testid="practice-cta" className="btn-primary !bg-[#C2A679] !border-[#C2A679] !text-[#0F172A] hover:!bg-[#D9C7A3]">
            Begin Case Review <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
